import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { BsEmojiSmile, BsPaperclip, BsSend } from "react-icons/bs";
import utilstopLogo from "../../assets/images/supports-logo.svg";
import supportIllustration from "../../assets/images/support-illustration.svg";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { usePubNub } from "pubnub-react";
import { toast } from "react-toastify";
import * as apicall from "../../components/Apicall";
import { debounce } from "lodash";
import Picker from "@emoji-mart/react";

const timeFormate = (time) => {
  // PubNub timetokens are 17-digit numbers representing nanoseconds since 1970
  // Convert to milliseconds by dividing by 10000 (or take first 13 digits)
  const timestampInMs =
    typeof time === "string"
      ? parseInt(time.substring(0, 13))
      : Math.floor(time / 10000000);

  const messageDate = new Date(timestampInMs);
  return messageDate
    .toLocaleString([], {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
    .replace(" at ", " ");
};
const generateMessageId = () => Math.random().toString(36).substring(2, 15);

// Helper to check if a message is a file
const isFileMessage = (msg) =>
  msg && typeof msg === "object" && msg.file && msg.file.url;

const MessageList = React.memo(({ item }) => (
  <div
    className={`d-flex mb-3 ${
      item.sender === 1 ? "justify-content-end" : "justify-content-start"
    }`}
  >
    {item.sender === 2 && (
      <div className="mr-2 align-self-end">
        <img src={utilstopLogo} alt="UtilStop" className="chat-logo-size" />
      </div>
    )}
    <div
      className="chat-receiver-style"
      style={{
        backgroundColor: item.sender === 2 ? "#FFF2F2" : "#F7F7F7",
      }}
    >
      <div
        style={{
          fontSize: "14px",
          lineHeight: "1.4",
          textAlign: "left",
        }}
      >
        {isFileMessage(item.message) ? (
          <a
            href={item.message.file.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            📎 {item.message.file.name || "Download file"}
          </a>
        ) : typeof item.message === "object" ? (
          item.message.description
        ) : (
          item.message
        )}
      </div>
      <div className="text-muted small mt-1">{timeFormate(item.time)}</div>
    </div>
  </div>
));

const Support = () => {
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const pubnub = usePubNub();
  const listenerInitialized = useRef(false);

  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [channel, setChannel] = useState("");
  const [userId, setUserId] = useState("");
  const [historyCount] = useState(50);
  const [startTimeToken, setStartTimeToken] = useState("");
  const [isConnected, setIsConnected] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const fileInputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  //function call to get the user id
  const getUserDetails = async () => {
    try {
      setLoading(true);
      const res = await apicall.get("vendor_chatInfo");
      if (res.success) {
        const user_id = res.data[0].id;
        const channel_id = "us_channel_" + user_id;
        const uuid = "us_" + user_id;

        setChannel(channel_id);
        setUserId(uuid);
        connectPubNub(channel_id, uuid);
        fetchHistory(channel_id, uuid, "");
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
      toast.error("Failed to load user information");
    } finally {
      setLoading(false);
    }
  };

  //function call to connect to the pubnub channel
  const connectPubNub = useCallback(
    (channel_id, uuid) => {
      if (!pubnub.getSubscribedChannels().includes(channel_id)) {
        pubnub.subscribe({ channels: [channel_id] });
      }

      if (!listenerInitialized.current) {
        pubnub.addListener({
          message: (messageEvent) => {
            if (messageEvent.channel === channel_id) {
              setMessages((prev) => {
                const isDuplicate = prev.some(
                  (msg) => msg.id === messageEvent.message?.id
                );

                if (!isDuplicate) {
                  return [
                    ...prev,
                    {
                      sender: messageEvent.publisher === uuid ? 1 : 2,
                      message: messageEvent.message.description,
                      time: messageEvent.timetoken,
                      timetoken: messageEvent.timetoken,
                      id: messageEvent.message?.id || messageEvent.timetoken,
                    },
                  ];
                }
                return prev;
              });
            }
          },
          status: (statusEvent) => {
            if (statusEvent.category === "PNConnectedCategory") {
              setIsConnected(true);
            } else if (statusEvent.category === "PNDisconnectedCategory") {
              setIsConnected(false);
            }
          },
        });
        listenerInitialized.current = true;
      }
    },
    [pubnub]
  );

  //function call to extract the messages from the api and setting it to the state
  const extractMessages = (messageList, uuid) => {
    const mesg = messageList.map((message) => ({
      sender: message.uuid === uuid ? 1 : 2,
      message: message.message.description,
      time: message.timetoken,
      timetoken: message.timetoken,
      id: message.message?.id || message.timetoken,
    }));
    setMessages((prev) => [...mesg, ...prev]);
  };

  //function call to fetch the message history of the previous messages
  const fetchHistory = async (channel_id, uuid, start_time) => {
    try {
      const response = await pubnub.fetchMessages({
        channels: [channel_id],
        stringifiedTimeToken: true,
        count: historyCount,
        start: start_time || undefined,
        includeTimetoken: true,
      });

      const messages = response.channels?.[channel_id] || [];
      extractMessages(messages, uuid);

      if (messages.length > 0) {
        setStartTimeToken(messages[messages.length - 1].timetoken);
      }
    } catch (error) {
      console.error("Error fetching message history:", error);
      toast.error("Failed to load chat history");
    }
  };

  const getOldHistory = useCallback(() => {
    if (startTimeToken && channel) {
      fetchHistory(channel, userId, startTimeToken);
    }
  }, [startTimeToken, channel, userId]);

  const handleScroll = useMemo(
    () =>
      debounce((e) => {
        if (!e?.currentTarget) return;
        const { scrollTop } = e.currentTarget;
        if (scrollTop === 0 && startTimeToken) {
          getOldHistory();
        }
      }, 300),
    [startTimeToken, getOldHistory]
  );

  useEffect(() => {
    getUserDetails();

    const scrollHandler = handleScroll;

    return () => {
      pubnub.unsubscribeAll();
      pubnub.removeAllListeners();
      listenerInitialized.current = false;
      scrollHandler.cancel();
    };
  }, []);
  //function call to send the messages or publish
  const publishMessage = async () => {
    if (!message.trim() || !channel) return;

    try {
      const messageId = generateMessageId();
      const tempMessage = {
        sender: 1,
        message: message,
        time: `${Date.now()}0000000`,
        timetoken: `${Date.now()}0000000`,
        id: messageId,
      };

      setMessages((prev) => [...prev, tempMessage]);
      setMessage("");

      await pubnub.publish({
        channel: channel,
        message: {
          description: message,
          id: messageId,
        },
      });
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      publishMessage();
    }
  };

  // Handle emoji select
  const handleEmojiSelect = (emoji) => {
    setMessage((prev) => prev + emoji.native);
    setShowEmojiPicker(false);
  };

  // Handle file selection and upload
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file && channel) {
      try {
        // Send file using PubNub
        const res = await pubnub.sendFile({
          channel: channel,
          file,
          message: { file: { name: file.name } },
        });
        // Add file message to UI
        setMessages((prev) => [
          ...prev,
          {
            sender: 1,
            message: { file: { url: res.file.url, name: file.name } },
            time: `${Date.now()}0000000`,
            timetoken: `${Date.now()}0000000`,
            id: generateMessageId(),
          },
        ]);
      } catch (err) {
        toast.error("Failed to send file");
      }
    }
    // Reset file input
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="container-fluid px-3 h-100">
      <div className="row h-100">
        {/* <div className="col-12 col-lg-8">
          <div
            className="card border-0 shadow-sm h-100"
            style={{ borderRadius: "8px" }}
          >
            <div
              className="card-body p-0 d-flex flex-column"
              style={{ height: "calc(100vh - 100px)" }}
            >
              <div className="p-3 border-bottom">
                {loading ? (
                  <div className="d-flex align-items-center gap-2">
                    <Skeleton circle width={40} height={40} />
                    <div>
                      <Skeleton width={120} height={20} className="mb-1" />
                      <Skeleton width={60} height={16} />
                    </div>
                  </div>
                ) : (
                  <div className="d-flex align-items-center gap-2">
                    <div
                      className="rounded-circle bg-white d-flex align-items-center justify-content-center"
                      style={{ width: "40px", height: "40px" }}
                    >
                      <img
                        src={utilstopLogo}
                        alt="UtilStop"
                        style={{ width: "24px", height: "24px" }}
                      />
                    </div>
                    <div>
                      <h6 className="mb-0">UtilStop Admin</h6>
                      <span className="d-flex align-items-center gap-1 small">
                        <span
                          className={`rounded-circle ${
                            isConnected ? "bg-success" : "bg-danger"
                          }`}
                          style={{ width: "6px", height: "6px" }}
                        ></span>
                        {isConnected ? "Online" : "Offline"}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div
                ref={messagesContainerRef}
                className="flex-grow-1 p-3 overflow-auto"
                onScroll={handleScroll}
                style={{ minHeight: "1px" }}
              >
                {loading ? (
                  <>
                    <div className="text-center mb-4">
                      <Skeleton width={60} height={16} />
                    </div>
                    {Array(4)
                      .fill(0)
                      .map((_, index) => (
                        <div key={index} className="mb-4">
                          <div
                            className={`d-flex ${
                              index % 2 === 0 ? "" : "justify-content-end"
                            }`}
                          >
                            <div style={{ maxWidth: "60%" }}>
                              <Skeleton
                                height={80}
                                style={{ borderRadius: "20px" }}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                  </>
                ) : (
                  <>
                    {messages.map((item) => (
                      <MessageList key={item.id} item={item} />
                    ))}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>

              <div className="p-3 border-top">
                {loading ? (
                  <Skeleton height={40} />
                ) : (
                  <div className="d-flex gap-2 align-items-center">
                    <div className="position-relative flex-grow-1">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Type a message"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        style={{
                          borderRadius: "20px",
                          paddingRight: "80px",
                        }}
                        onFocus={() => setShowEmojiPicker(false)}
                      />

                      {showEmojiPicker && (
                        <div
                          style={{
                            position: "absolute",
                            bottom: 50,
                            right: 0,
                            zIndex: 9999,
                          }}
                        >
                          <Picker onEmojiSelect={handleEmojiSelect} />
                        </div>
                      )}
                      <div className="position-absolute end-0 top-50 translate-middle-y d-flex gap-2 me-2">
                        <button
                          className="btn btn-link p-1 text-muted"
                          type="button"
                          onClick={() => setShowEmojiPicker((v) => !v)}
                        >
                          <BsEmojiSmile size={18} />
                        </button>
                        <button
                          className="btn btn-link p-1 text-muted"
                          type="button"
                          onClick={() =>
                            fileInputRef.current && fileInputRef.current.click()
                          }
                        >
                          <BsPaperclip size={18} />
                        </button>
                        <input
                          type="file"
                          ref={fileInputRef}
                          style={{ display: "none" }}
                          onChange={handleFileChange}
                        />
                      </div>
                    </div>
                    <button
                      className="btn btn-primary rounded-circle d-flex align-items-center justify-content-center"
                      onClick={publishMessage}
                      disabled={!message.trim()}
                      style={{ width: "40px", height: "40px" }}
                    >
                      <BsSend size={18} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div> */}

        {/* <div className="col-12 col-lg-4"> */}
        <div className="col-12 ">
          <div
            className="card border-0 shadow-sm h-100"
            style={{ borderRadius: "8px" }}
          >
            {/* <div className="card-body p-3 p-md-4"> */}
            <div className="d-flex justify-content-center align-items-center flex-column  p-3 p-md-4">
              {loading ? (
                <>
                  <div className="text-center mb-3 mb-md-4">
                    <Skeleton height={200} />
                  </div>
                  <Skeleton height={24} width={150} className="mb-3 mb-md-4" />
                  <div className="d-flex flex-column gap-2 gap-md-3">
                    <div className="d-flex flex-wrap align-items-center gap-1 gap-md-2">
                      <Skeleton width={100} height={16} />
                      <Skeleton width={120} height={16} />
                    </div>
                    <div className="d-flex flex-wrap align-items-center gap-1 gap-md-2">
                      <Skeleton width={100} height={16} />
                      <Skeleton width={150} height={16} />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-center mb-3 mb-md-4">
                    <img
                      src={supportIllustration}
                      alt="Support"
                      className="img-fluid"
                      style={{ maxHeight: "200px" }}
                    />
                  </div>
                  <h5 className="mb-3 mb-md-4 text-center text-md-start">
                    Connect with Us
                  </h5>
                  <div className="d-flex flex-column gap-2 gap-md-3">
                    <div className="d-flex flex-wrap align-items-center gap-1 gap-md-2">
                      <span className="text-muted small-md">
                        Mobile Number:
                      </span>
                      <span className="font-weight-bold text-break">
                        +971 563791724
                      </span>
                    </div>
                    <div className="d-flex flex-wrap align-items-center gap-1 gap-md-2">
                      <span className="text-muted small-md">
                        Email Address:
                      </span>
                      <span className="font-weight-bold text-break">
                        support@utilstop.com
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Support;
